import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AddArtworkCSS from "../css/AddArtwork.module.css";
import {
  cropImageToDataUrl,
  revokeObjectUrl,
} from "../utils/cropImage";

const DEFAULT_PERCENT_CROP = {
  unit: "%",
  x: 5,
  y: 5,
  width: 90,
  height: 90,
};

const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;

function AddArtwork() {
  const [authState, setAuthState] = useState("checking");
  const [pin, setPin] = useState("");
  const [crop, setCrop] = useState(DEFAULT_PERCENT_CROP);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadSession() {
      try {
        const response = await fetch("/api/admin/session", {
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error("Unable to check upload session.");
        }

        const payload = await response.json();

        if (isMounted) {
          setAuthState(payload.authenticated ? "authenticated" : "locked");
        }
      } catch (error) {
        if (isMounted) {
          setAuthState("locked");
        }
      }
    }

    loadSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => () => revokeObjectUrl(imageUrl), [imageUrl]);

  const canPublish =
    authState === "authenticated" &&
    Boolean(imageUrl) &&
    completedCrop &&
    completedCrop.width >= 40 &&
    completedCrop.height >= 40 &&
    !isPublishing;

  const statusText = useMemo(() => {
    if (authState === "checking") {
      return "Checking access...";
    }

    if (authState !== "authenticated") {
      return "Enter the family upload PIN to unlock this page.";
    }

    if (isPublishing) {
      return "Uploading artwork and creating the GitHub commit...";
    }

    if (publishResult?.status === "success") {
      return "Published. Vercel will show the new artwork after the deploy finishes.";
    }

    return "Choose a photo, adjust the crop, and publish it to the gallery.";
  }, [authState, isPublishing, publishResult]);

  const resetArtworkForm = () => {
    setIsDragActive(false);
    revokeObjectUrl(imageUrl);
    imgRef.current = null;
    setSelectedFile(null);
    setImageUrl("");
    setCompletedCrop(null);
    setPublishResult(null);
    setCrop(DEFAULT_PERCENT_CROP);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleIncomingFile = (file) => {
    revokeObjectUrl(imageUrl);
    setPublishResult(null);
    setCompletedCrop(null);
    setCrop(DEFAULT_PERCENT_CROP);
    setIsDragActive(false);

    if (!file) {
      setSelectedFile(null);
      setImageUrl("");
      return false;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Choose an image file.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return false;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error("Choose an image smaller than 15 MB.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return false;
    }

    const nextUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setImageUrl(nextUrl);
    return true;
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!pin.trim()) {
      toast.error("Enter the upload PIN.");
      return;
    }

    setIsAuthenticating(true);

    try {
      const response = await fetch("/api/admin/session", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pin: pin.trim() }),
      });

      const payload = await response.json();

      if (!response.ok || !payload.authenticated) {
        toast.error(payload.message || "That PIN did not work.");
        return;
      }

      setPin("");
      setAuthState("authenticated");
      toast.success("Upload page unlocked.");
    } catch (error) {
      toast.error("Unable to verify the PIN right now.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/api/admin/session", {
        method: "DELETE",
        credentials: "same-origin",
      });
    } catch (error) {
      // Ignore logout errors and clear local state anyway.
    }

    resetArtworkForm();
    setAuthState("locked");
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    const wasAccepted = handleIncomingFile(file);

    if (!wasAccepted && event.target) {
      event.target.value = "";
    }
  };

  const handlePublish = async () => {
    if (!imgRef.current || !completedCrop) {
      toast.error("Choose a crop before publishing.");
      return;
    }

    setIsPublishing(true);
    setPublishResult(null);

    try {
      const imageDataUrl = await cropImageToDataUrl(
        imgRef.current,
        completedCrop
      );

      const response = await fetch("/api/artworks", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imageDataUrl,
          sourceFilename: selectedFile?.name || "artwork-upload.jpg",
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setAuthState("locked");
        }

        throw new Error(payload.message || "Unable to publish artwork.");
      }

      setPublishResult({
        status: "success",
        filename: payload.filename,
      });
      toast.success("Artwork published. Waiting for Vercel to deploy.");
    } catch (error) {
      setPublishResult({
        status: "error",
        message: error.message,
      });
      toast.error(error.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleAddAnotherArtwork = () => {
    resetArtworkForm();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleDropZoneKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    handleIncomingFile(file);
  };

  return (
    <div className={AddArtworkCSS.page}>
      <Header />
      <main className={AddArtworkCSS.main}>
        <section className={AddArtworkCSS.intro}>
          <p className={AddArtworkCSS.kicker}>Private Upload</p>
          <h1 className={AddArtworkCSS.title}>Add new artwork</h1>
          <p className={AddArtworkCSS.description}>{statusText}</p>
        </section>

        {authState !== "authenticated" ? (
          <section className={AddArtworkCSS.workspace}>
            <form className={AddArtworkCSS.pinForm} onSubmit={handleLogin}>
              <label className={AddArtworkCSS.label} htmlFor="upload-pin">
                Upload PIN
              </label>
              <input
                id="upload-pin"
                type="password"
                value={pin}
                onChange={(event) => setPin(event.target.value)}
                className={AddArtworkCSS.input}
                autoComplete="current-password"
                disabled={isAuthenticating || authState === "checking"}
              />
              <button
                type="submit"
                className={AddArtworkCSS.primaryButton}
                disabled={isAuthenticating || authState === "checking"}
              >
                {isAuthenticating ? "Unlocking..." : "Unlock upload page"}
              </button>
            </form>
          </section>
        ) : (
          <section className={AddArtworkCSS.workspace}>
            <div className={AddArtworkCSS.toolbar}>
              <label className={AddArtworkCSS.filePicker}>
                <span>Choose artwork photo</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={AddArtworkCSS.fileInput}
                />
              </label>
              <button
                type="button"
                onClick={handleLogout}
                className={AddArtworkCSS.secondaryButton}
              >
                Lock page
              </button>
            </div>

            <p className={AddArtworkCSS.helperText}>
              One image at a time. The crop becomes the published artwork, then
              the processed file is committed to GitHub for Vercel to deploy.
            </p>

            {selectedFile ? (
              <p className={AddArtworkCSS.fileName}>{selectedFile.name}</p>
            ) : null}

            {imageUrl ? (
              <div className={AddArtworkCSS.cropArea}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                  keepSelection
                  minWidth={40}
                  minHeight={40}
                  ruleOfThirds
                  className={AddArtworkCSS.cropper}
                >
                  <img
                    ref={imgRef}
                    src={imageUrl}
                    alt="Artwork crop preview"
                    className={AddArtworkCSS.previewImage}
                    onLoad={() => {
                      setCrop(DEFAULT_PERCENT_CROP);
                    }}
                  />
                </ReactCrop>
              </div>
            ) : (
              <div
                className={`${AddArtworkCSS.emptyState} ${
                  isDragActive ? AddArtworkCSS.emptyStateActive : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={handleDropZoneKeyDown}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <p className={AddArtworkCSS.emptyStateTitle}>
                  Drag and drop a picture to start the crop
                </p>
                <p className={AddArtworkCSS.emptyStateText}>
                  or click here to choose one from this device.
                </p>
              </div>
            )}

            <div className={AddArtworkCSS.actions}>
              <button
                type="button"
                onClick={handlePublish}
                className={AddArtworkCSS.primaryButton}
                disabled={!canPublish}
              >
                {isPublishing ? "Publishing..." : "Publish artwork"}
              </button>
            </div>

            {publishResult?.status === "success" ? (
              <div className={AddArtworkCSS.successMessage}>
                <p>
                  {publishResult.filename} is queued for the next deploy which
                  can take a couple of minutes.
                </p>
                <p>
                  After a couple of minutes{" "}
                  <Link to="/" className={AddArtworkCSS.resultLink}>
                    go here to see the added artwork
                  </Link>{" "}
                  or{" "}
                  <button
                    type="button"
                    onClick={handleAddAnotherArtwork}
                    className={AddArtworkCSS.inlineButton}
                  >
                    add another artwork
                  </button>
                  .
                </p>
              </div>
            ) : null}

            {publishResult?.status === "error" ? (
              <p className={AddArtworkCSS.errorMessage}>
                {publishResult.message}
              </p>
            ) : null}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default AddArtwork;
